import React, { useState, useEffect } from 'react';
import { X, Activity, Users, FileText, Building2, HardDrive, Calendar } from 'lucide-react';
import apiService from '../../services/api';
import { CompanyActivityStats, CompanyUsageStats } from '../../types';
import toast from 'react-hot-toast';

interface CompanyActivityModalProps {
  companyId: number;
  companyName: string;
  isOpen: boolean;
  onClose: () => void;
}

const CompanyActivityModal: React.FC<CompanyActivityModalProps> = ({
  companyId,
  companyName,
  isOpen,
  onClose
}) => {
  const [activityStats, setActivityStats] = useState<CompanyActivityStats | null>(null);
  const [usageStats, setUsageStats] = useState<CompanyUsageStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && companyId) {
      loadStats();
    }
  }, [isOpen, companyId]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const [activityResponse, usageResponse] = await Promise.all([
        apiService.getCompanyActivityStats(companyId),
        apiService.getCompanyUsageStats(companyId)
      ]);

      if (activityResponse.success && activityResponse.data) {
        setActivityStats(activityResponse.data);
      }

      if (usageResponse.success && usageResponse.data) {
        setUsageStats(usageResponse.data);
      }
    } catch (error) {
      console.error('Error loading company stats:', error);
      toast.error('Failed to load company statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'Healthy':
        return 'text-green-600 dark:text-green-400';
      case 'At Risk':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'Inactive':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Activity & Usage Statistics - {companyName}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Activity Statistics */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Activity Statistics
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Health Status</div>
                      <div className={`text-lg font-semibold mt-1 ${getHealthStatusColor(activityStats?.healthStatus || '')}`}>
                        {activityStats?.healthStatus || 'Unknown'}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Last Login</div>
                      <div className="text-lg font-semibold mt-1 text-gray-900 dark:text-gray-100">
                        {formatDate(activityStats?.lastLoginDate)}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Last Activity</div>
                      <div className="text-lg font-semibold mt-1 text-gray-900 dark:text-gray-100">
                        {formatDate(activityStats?.lastActivityDate)}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Active Users (Last 30 Days)</div>
                      <div className="text-lg font-semibold mt-1 text-gray-900 dark:text-gray-100">
                        {activityStats?.activeUsersLast30Days || 0}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Usage Statistics */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                    <HardDrive className="h-5 w-5" />
                    Usage Statistics
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Total Work Orders
                      </div>
                      <div className="text-2xl font-bold mt-1 text-gray-900 dark:text-gray-100">
                        {usageStats?.totalWorkOrders || 0}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {usageStats?.workOrdersLast30Days || 0} in last 30 days
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Total Invoices
                      </div>
                      <div className="text-2xl font-bold mt-1 text-gray-900 dark:text-gray-100">
                        {usageStats?.totalInvoices || 0}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {usageStats?.invoicesLast30Days || 0} in last 30 days
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Active Users
                      </div>
                      <div className="text-2xl font-bold mt-1 text-gray-900 dark:text-gray-100">
                        {usageStats?.activeUsers || 0}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Total Clients
                      </div>
                      <div className="text-2xl font-bold mt-1 text-gray-900 dark:text-gray-100">
                        {usageStats?.totalClients || 0}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Total Vendors
                      </div>
                      <div className="text-2xl font-bold mt-1 text-gray-900 dark:text-gray-100">
                        {usageStats?.totalVendors || 0}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                        <HardDrive className="h-4 w-4" />
                        Storage Used
                      </div>
                      <div className="text-2xl font-bold mt-1 text-gray-900 dark:text-gray-100">
                        {usageStats?.storageUsedFormatted || '0 B'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Work Order Date Range */}
                {(usageStats?.oldestWorkOrderDate || usageStats?.mostRecentWorkOrderDate) && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Work Order Date Range
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                        <div className="text-sm text-gray-500 dark:text-gray-400">Oldest Work Order</div>
                        <div className="text-lg font-semibold mt-1 text-gray-900 dark:text-gray-100">
                          {formatDate(usageStats?.oldestWorkOrderDate)}
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                        <div className="text-sm text-gray-500 dark:text-gray-400">Most Recent Work Order</div>
                        <div className="text-lg font-semibold mt-1 text-gray-900 dark:text-gray-100">
                          {formatDate(usageStats?.mostRecentWorkOrderDate)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="btn-primary w-full sm:ml-3 sm:w-auto"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyActivityModal;

